import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { makeRequest } from "../../utils/request";

export interface TestNotificationsState {
  notifications: Record<string, string[]>; // agentId -> testNames[]
  isLoading: Record<string, boolean>;
  error: Record<string, string | null>;
}

const initialState: TestNotificationsState = {
  notifications: {},
  isLoading: {},
  error: {},
};

export const fetchTestNotifications = createAsyncThunk("testNotifications/fetchNotifications", async (agentId: string) => {
  const response = await makeRequest<{ data: { notifications: string[] } }>(`/v1/agentTestAnalytics/notifications?agentId=${agentId}`);
  return { agentId, notifications: response.data?.notifications || [] };
});

export const dismissTestNotification = createAsyncThunk(
  "testNotifications/dismissNotification",
  async ({ agentId, testName }: { agentId: string; testName: string }) => {
    await makeRequest(`/v1/agentTestAnalytics/notification`, {
      method: "POST",
      body: { agentId, testName },
    });
    return { agentId, testName };
  },
);

const testNotificationsSlice = createSlice({
  name: "testNotifications",
  initialState,
  reducers: {
    clearNotifications: (state, action: PayloadAction<string>) => {
      const agentId = action.payload;
      delete state.notifications[agentId];
      delete state.isLoading[agentId];
      delete state.error[agentId];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTestNotifications.pending, (state, action) => {
        const agentId = action.meta.arg;
        state.isLoading[agentId] = true;
        state.error[agentId] = null;
      })
      .addCase(fetchTestNotifications.fulfilled, (state, action) => {
        const { agentId, notifications } = action.payload;
        state.notifications[agentId] = notifications;
        state.isLoading[agentId] = false;
        state.error[agentId] = null;
      })
      .addCase(fetchTestNotifications.rejected, (state, action) => {
        const agentId = action.meta.arg;
        state.isLoading[agentId] = false;
        state.error[agentId] = action.error.message || "Failed to fetch notifications";
      })
      .addCase(dismissTestNotification.fulfilled, (state, action) => {
        const { agentId, testName } = action.payload;
        if (state.notifications[agentId]) {
          state.notifications[agentId] = state.notifications[agentId].filter((name) => name !== testName);
        }
      });
  },
});

export const { clearNotifications } = testNotificationsSlice.actions;
export default testNotificationsSlice.reducer;
